import React, { useRef } from 'react';
import { Close, Menu } from '@mui/icons-material';
import {
  Avatar,
  LinearProgress,
  Popover,
  Stack,
  Typography,
} from '@mui/material';
import { styled, useTheme } from '@mui/material/styles';
import { appConfig } from 'utils/config';

import MenuUserItem from './MenuUserItem';

// ------------------ styled components --------------------

const CustomLinearProgress = styled(LinearProgress)(({ theme }) => ({
  backgroundColor: theme.palette.grey[5],
  '& span': {
    backgroundColor: theme.palette.green.primary,
  },
}));

// --------------------- component -------------------------

const MenuUser = ({
  user,
  open,
  setOpen,
  setOverlay,
  logOut,
  isExamPage = false,
  progress = false,
  progressTitle,
  ...props
}) => {
  const { baseUrl } = appConfig;
  const theme = useTheme();

  const anchorRef = useRef(null);

  // toggle to open/close user menu
  const handleToggle = () => {
    setOpen((prev) => !prev);
    setOverlay((prev) => !prev);
  };

  // to close user menu when user clicks outside the component
  const handleClose = (event) => {
    if (anchorRef.current && anchorRef.current.contains(event.target)) {
      return;
    }
    setOpen(false);
    setOverlay(false);
  };

  // render student learning and exam progress
  const renderProgress = () => {
    return (
      <Stack
        sx={{
          width: '280px',
          height: '100%',
          px: '24px',
          borderLeft: `2px solid ${theme.palette.grey[6]}`,
          gap: '6px',
        }}
        justifyContent="center"
      >
        <Stack direction="row" justifyContent="space-between">
          <Typography sx={{ typography: 'captionmedium12', color: 'grey.1' }}>
            {progressTitle}
          </Typography>
          <Typography sx={{ typography: 'caption12', color: 'grey.3' }}>
            {progress}/100%
          </Typography>
        </Stack>

        <CustomLinearProgress variant="determinate" value={progress} />
      </Stack>
    );
  };

  // to render navbar action in dashboard, student learning, and exam
  const renderNavbarAction = () => {
    // burger dropdown navbar action
    if (isExamPage) {
      return (
        <>
          {!Number.isNaN(progress) && renderProgress()}
          <Stack
            sx={{
              width: '70px',
              height: '100%',
              bgcolor: theme.palette.green.primary,
              color: theme.palette.grey[0],
              justifyContent: 'center',
              alignItems: 'center',
              cursor: 'pointer',
              mr: '-24px',
            }}
            onClick={handleToggle}
            ref={anchorRef}
          >
            {open ? <Close /> : <Menu />}
          </Stack>
        </>
      );
    }

    // avatar navbar action
    return (
      <Avatar
        ref={anchorRef}
        src={
          user.profilePicture !== '/defaultprofilepicture.png'
            ? baseUrl + user.profilePicture
            : 'static/icons/ic_profile.svg'
        }
        sx={{
          width: { xs: 24, md: 32 },
          height: { xs: 24, md: 32 },
          cursor: 'pointer',
        }}
        onClick={handleToggle}
      />
    );
  };

  return (
    <Stack
      sx={{
        flexDirection: 'row',
        alignItems: 'center',
        gap: '8px',
        height: '100%',
      }}
      {...props}
    >
      {renderNavbarAction()}

      <Popover
        open={open}
        onClose={handleClose}
        disableScrollLock
        anchorEl={anchorRef.current}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        sx={{
          mt: { xs: '40px', md: '24px' },
          '& .MuiPaper-root': { borderRadius: '8px' },
        }}
      >
        <MenuUserItem user={user} logOut={logOut} closeMenu={handleToggle} />
      </Popover>
    </Stack>
  );
};

export default MenuUser;
